import { type FC } from "react";
import { Checkbox, InputNumber } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import TableHeaderCell from "../shared/TableHeaderCell";
import "./style.less";

export interface GroupTableRow {
  id: string;
  group: string;
  plot: string;
  seedRate?: number;
  checked?: boolean;
}

export interface GroupTableProps {
  rows: GroupTableRow[];
  onToggleRow?: (id: string, checked: boolean) => void;
  onShowPlot?: (id: string) => void;
  onSeedRateChange?: (id: string, value: number | null) => void;
  className?: string;
  showCheckbox?: boolean;
  showSeedRate?: boolean;
  showHeaderIcons?: boolean;
  // A fixed accent applied to every row (e.g. a single treatment's colour),
  // `null` for the neutral/untreated colour, or left out to keep the legacy
  // per-row round-robin colouring.
  accentColorIndex?: number | null;
  accentColor?: string;
  groupColumnLabel?: string;
  groupColumnUnit?: string;
  plotColumnLabel?: string;
  plotColumnUnit?: string;
}

const getAccentClassName = (
  index: number,
  accentColorIndex?: number | null
): string => {
  if (accentColorIndex === null) {
    return "";
  }
  if (accentColorIndex != null) {
    return `rd-group-table__accent--${accentColorIndex}`;
  }
  return `rd-group-table__accent--${(index % 4) + 1}`;
};

const GroupTable: FC<GroupTableProps> = ({
  rows,
  onToggleRow,
  onShowPlot,
  onSeedRateChange,
  className,
  showCheckbox = true,
  showSeedRate = true,
  showHeaderIcons = true,
  accentColorIndex,
  accentColor,
  groupColumnLabel,
  groupColumnUnit,
  plotColumnLabel,
  plotColumnUnit,
}) => {
  const { t } = useTranslation();
  const { isOutlined, toggleOutline } = usePlotOutline();

  return (
    <div
      className={`rd-group-table ${
        showSeedRate ? "" : "rd-group-table--compact"
      } ${className ?? ""}`}
      data-testid="group-table"
      role="table"
    >
      <div className="rd-group-table__header" role="row">
        <TableHeaderCell
          classPrefix="rd-group-table"
          label={
            groupColumnLabel ??
            formatTranslation(t("newRatesAndDosage.groupTable.group"))
          }
          unit={
            groupColumnUnit ??
            formatTranslation(t("newRatesAndDosage.groupTable.groupUnit"))
          }
          columnClassName="rd-group-table__col--group"
          showIcons={showHeaderIcons}
        />
        <TableHeaderCell
          classPrefix="rd-group-table"
          label={
            plotColumnLabel ??
            formatTranslation(t("newRatesAndDosage.groupTable.plot"))
          }
          unit={
            plotColumnUnit ??
            formatTranslation(t("newRatesAndDosage.groupTable.plotUnit"))
          }
          columnClassName="rd-group-table__col--plot"
          showIcons={showHeaderIcons}
        />
        {showSeedRate && (
          <TableHeaderCell
            classPrefix="rd-group-table"
            label={formatTranslation(
              t("newRatesAndDosage.groupTable.seedRate")
            )}
            unit={formatTranslation(
              t("newRatesAndDosage.groupTable.seedRateUnit")
            )}
            columnClassName="rd-group-table__col--seed-rate"
            showIcons={showHeaderIcons}
          />
        )}
        <div
          className="rd-group-table__header-cell rd-group-table__col--show"
          aria-hidden="true"
        />
      </div>
      <div className="rd-group-table__body">
        {rows.map((row, index) => (
          <div
            key={row.id}
            className="rd-group-table__row"
            role="row"
            data-testid="group-table-row"
          >
            <div
              className="rd-group-table__cell rd-group-table__col--group"
              role="cell"
            >
              <span
                className={`rd-group-table__accent ${getAccentClassName(
                  index,
                  accentColorIndex
                )}`}
                style={{ backgroundColor: accentColor }}
                aria-hidden="true"
              />
              {showCheckbox && (
                <Checkbox
                  checked={row.checked}
                  onChange={(e) => onToggleRow?.(row.id, e.target.checked)}
                  aria-label={formatTranslation(
                    t("newRatesAndDosage.groupTable.selectRow", {
                      group: row.group,
                    })
                  )}
                />
              )}
              <span className="rd-group-table__group-label">{row.group}</span>
            </div>
            <div
              className="rd-group-table__cell rd-group-table__col--plot"
              role="cell"
            >
              <span className="rd-group-table__plot-label">{row.plot}</span>
            </div>
            {showSeedRate && (
              <div
                className="rd-group-table__cell rd-group-table__col--seed-rate"
                role="cell"
              >
                <InputNumber
                  className="rd-group-table__rate-input"
                  value={row.seedRate}
                  controls
                  onChange={(value) => onSeedRateChange?.(row.id, value)}
                  aria-label={formatTranslation(
                    t("newRatesAndDosage.groupTable.seedRateInput", {
                      plot: row.plot,
                    })
                  )}
                />
              </div>
            )}
            <div
              className="rd-group-table__cell rd-group-table__col--show"
              role="cell"
            >
              <button
                type="button"
                className="rd-group-table__show-btn"
                aria-label={formatTranslation(
                  t("newRatesAndDosage.groupTable.showPlot", {
                    plot: row.plot,
                  })
                )}
                aria-pressed={isOutlined(row.id)}
                onClick={() => {
                  toggleOutline(row.id);
                  onShowPlot?.(row.id);
                }}
              >
                {isOutlined(row.id) ? (
                  <EyeOutlined />
                ) : (
                  <EyeInvisibleOutlined />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GroupTable;
